import React, { createContext, useContext, ReactNode } from 'react';
import { ExerciseLog } from '../Clients/reminds';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClientContext } from './ApiClient.Context';

interface ContextType {
  data: ExerciseLog[];
  addExerciseLog: (exerciseLog: ExerciseLog) => void;
  deleteExerciseLog: (id: number) => void;
}

const Context = createContext<ContextType | undefined>(undefined);

export const useExerciseLogsContext = () => {
  const context = useContext(Context);
  if (!context) {
    throw new Error('useExerciseLogsContext must be used within an ExerciseLogsProvider');
  }
  return context;
};

interface Props {
  children: ReactNode;
}

export const ExerciseLogsProvider: React.FC<Props> = ({ children }) => {
  const { exerciseLogsClient } = useApiClientContext();
  const queryClient = useQueryClient();

  const { data } = useQuery({
    queryKey: ['exerciseLogs'],
    queryFn: () => exerciseLogsClient.getExerciseLogs(),
  });

  const { mutate: addExerciseLog } = useMutation({
    mutationFn: (exerciseLog: ExerciseLog) => exerciseLogsClient.createExerciseLog(exerciseLog),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exerciseLogs'] });
    },
    onError: error => {
      console.error('Error adding exercise log:', error);
    },
  });

  const { mutate: deleteExerciseLog } = useMutation({
    mutationFn: (id: number) => exerciseLogsClient.deleteExerciseLog(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exerciseLogs'] });
    },
    onError: error => {
      console.error('Error deleting exercise log:', error);
    },
  });

  return (
    <Context.Provider value={{ data: data || [], addExerciseLog, deleteExerciseLog }}>{children}</Context.Provider>
  );
};
